import { useEffect, useMemo } from 'react';

import { useAtom } from 'jotai';

import type {
  ImageAnalyses,
  LatestImageBoneLevels,
  LatestImageCalculus,
  LatestImagePeriapicalRadiolucency,
  LatestImagesCaries,
  LatestImagesTeethV2,
} from '../../../../@types/latestImagesV2';
import type { TClaimTooth, TFlattenedTreatment } from '../../../../@types/rcm';
import { includeFindingTypeAtom } from '../state';
import type { TIncludeTypes, TView } from '../state';

import type { TSupportedTreatmentType } from './useSummary';

export type TImageFilter = {
  includeCariousLesions: boolean;
  includeRadiographicBoneLoss: boolean;
  includeDmf: boolean;
  includePeriapicalRadiolucency: boolean;
  includeBitewingOnly: boolean;
};

export type TFindingType = {
  hasCaris: boolean;
  hasBoneLoss: boolean;
  hasCalculus: boolean;
  hasPeriapicalRadiolucency: boolean;
};

export type FilteredImage = ImageAnalyses & {
  teeth: FilteredTooth[]; // Adjust this based on the actual type returned by getFilteredTeeth
};

export type FilteredTooth = LatestImagesTeethV2 & {
  caries: LatestImagesCaries[];
  boneLevels: LatestImageBoneLevels[];
  calculus: LatestImageCalculus[];
  periapicalRadiolucency: LatestImagePeriapicalRadiolucency[];
};

// TODO: Move methods inside hook or to helpers.ts

const shouldIncludeFindingType = (
  type: keyof TFindingType,
  hasFindings: boolean,
  selectedTreatmentGroups: string[],
): boolean => {
  if (
    selectedTreatmentGroups.includes('srp') &&
    selectedTreatmentGroups.length === 1
  ) {
    return type === 'hasBoneLoss' && hasFindings;
  } else {
    return hasFindings;
  }
};

// TODO: add return type
const getFilteredTeeth = (input: {
  teeth: LatestImagesTeethV2[];
  includedFindingTypes: TImageFilter;
}): FilteredTooth[] => {
  const { teeth, includedFindingTypes } = input;
  return teeth.map((tooth) => ({
    ...tooth,
    caries: includedFindingTypes.includeCariousLesions ? tooth.caries : [],
    boneLevels: includedFindingTypes.includeRadiographicBoneLoss
      ? tooth.boneLevels
      : [],
    calculus: includedFindingTypes.includeRadiographicBoneLoss
      ? tooth.calculus
      : [],
    periapicalRadiolucency: includedFindingTypes.includePeriapicalRadiolucency
      ? tooth.periapicalRadiolucency
      : [],
  }));
};

const getImagesWithTeethMatchingTx = (input: {
  images: ImageAnalyses[];
  selectedTeeth: string[];
  defaultTeeth: TClaimTooth[];
  selectedTreatments: TFlattenedTreatment[];
  defaultView: TView;
}): ImageAnalyses[] => {
  const {
    images,
    selectedTeeth,
    defaultTeeth,
    selectedTreatments,
    defaultView,
  } = input;

  const filteredImages = images
    .map((image: ImageAnalyses) => {
      const teeth = image.teeth.filter((tooth) => {
        if (defaultView === 'image') {
          return true;
        }
        if (!selectedTeeth.includes(tooth.toothId)) {
          return false;
        }

        const toothObj = defaultTeeth.find(
          (defaultTooth) => defaultTooth.toothId === tooth.toothId,
        );
        const treatments = toothObj?.treatments;

        const treatmentsSelected = treatments?.filter((treatment) =>
          selectedTreatments
            .map((selectedTreatment) => selectedTreatment.txId)
            .includes(treatment.txId),
        );

        return treatmentsSelected?.some((treatment) =>
          treatment.imageIds.includes(image.imageId),
        );
      });

      return {
        ...image,
        teeth,
      };
    })
    // TODO: Add image type
    .filter((img) => img.teeth.length > 0);

  return filteredImages;
};

const getPresentFindingTypes = (input: {
  images: ImageAnalyses[];
}): TFindingType => {
  const { images } = input;
  const hasCaris = images.some((image) =>
    image.teeth.some((tooth) => tooth.caries.length > 0),
  );
  const hasBoneLoss = images.some((image) =>
    image.teeth.some((tooth) => tooth.boneLevels.length > 0),
  );
  const hasCalculus = images.some((image) =>
    image.teeth.some((tooth) => tooth.calculus.length > 0),
  );
  const hasPeriapicalRadiolucency = images.some((image) =>
    image.teeth.some((tooth) => tooth.periapicalRadiolucency.length > 0),
  );
  return {
    hasCaris,
    hasBoneLoss,
    hasCalculus,
    hasPeriapicalRadiolucency,
  };
};

export const getTeethInImages = (images: ImageAnalyses[]) => {
  const allTeeth = images.flatMap((img) =>
    img.teeth.map((tooth) => tooth.toothId),
  );
  return [...new Set(allTeeth)];
};

export const getDefaultSelectedTeeth = (input: {
  images: ImageAnalyses[];
  treatmentTeeth?: string[];
  supportedTreatmentType: TSupportedTreatmentType;
}) => {
  const { images, supportedTreatmentType, treatmentTeeth } = input;
  let allTeeth: string[];

  if (supportedTreatmentType === 'SRP') {
    allTeeth = images.flatMap((img) =>
      img.teeth
        .filter((tooth) => tooth.matchesTreatment && tooth.meetsSrpCriteria)
        .map((tooth) => tooth.toothId),
    );
    if (treatmentTeeth) {
      allTeeth = allTeeth.filter((tooth) => treatmentTeeth.includes(tooth));
    }
  } else {
    allTeeth = getTeethInImages(images);

    if (treatmentTeeth) {
      allTeeth = allTeeth.filter((tooth) => treatmentTeeth.includes(tooth));
    }
  }
  return [...new Set(allTeeth)];
};

export const useFilteredImages = ({
  images,
  selectedTeeth,
  defaultTeeth,
  selectedTreatments,
  selectedTxGroups,
  imagingVisitId,
  defaultView,
}: {
  images: ImageAnalyses[];
  selectedTxGroups: string[];
  selectedTeeth: string[];
  defaultTeeth: TClaimTooth[];
  selectedTreatments: TFlattenedTreatment[];
  imagingVisitId: string;
  defaultView: TView;
}) => {
  const [includeFindingTypes, setIncludeFindingTypes] = useAtom(
    includeFindingTypeAtom,
  );

  const imagesWithTeethMatchingTxs = useMemo(
    () =>
      getImagesWithTeethMatchingTx({
        images,
        selectedTeeth,
        defaultTeeth,
        selectedTreatments,
        defaultView,
      }),
    [images, selectedTeeth, defaultTeeth, selectedTreatments, defaultView],
  );

  const presentFindingTypes = useMemo(
    () =>
      getPresentFindingTypes({
        images: imagesWithTeethMatchingTxs,
      }),
    [imagesWithTeethMatchingTxs],
  );

  const includedFindingTypes = useMemo(
    () => ({
      includeBitewingOnly: includeFindingTypes.includeBitewingsOnly,
      includeCariousLesions: includeFindingTypes.includeCariousLesions,
      includeDmf: includeFindingTypes.includeDmf,
      includePeriapicalRadiolucency:
        includeFindingTypes.includePeriapicalRadiolucency,
      includeRadiographicBoneLoss:
        includeFindingTypes.includeRadiographicBoneLoss,
    }),
    [includeFindingTypes],
  );

  const filteredImages: FilteredImage[] = useMemo(
    () =>
      imagesWithTeethMatchingTxs
        .filter((img) =>
          includeFindingTypes.includeBitewingsOnly
            ? img.imageType === 'BITEWING'
            : true,
        )
        .map((img) => ({
          ...img,
          teeth: getFilteredTeeth({
            teeth: img.teeth,
            includedFindingTypes,
          }),
        })),
    [
      imagesWithTeethMatchingTxs,
      includeFindingTypes.includeBitewingsOnly,
      includedFindingTypes,
    ],
  );

  useEffect(() => {
    if (filteredImages.length) {
      const findingTypes = {
        includeCariousLesions: shouldIncludeFindingType(
          'hasCaris',
          presentFindingTypes.hasCaris,
          selectedTxGroups,
        ),
        includePeriapicalRadiolucency: shouldIncludeFindingType(
          'hasPeriapicalRadiolucency',
          presentFindingTypes.hasPeriapicalRadiolucency,
          selectedTxGroups,
        ),
        includeRadiographicBoneLoss: shouldIncludeFindingType(
          'hasBoneLoss',
          presentFindingTypes.hasBoneLoss,
          selectedTxGroups,
        ),
        includeDmf: imagingVisitId ? true : !selectedTxGroups.includes('srp'),
      };

      Object.entries(findingTypes).forEach(([key, value]) => {
        setIncludeFindingTypes(key as keyof TIncludeTypes, value);
      });
    }
  }, [
    filteredImages.length,
    presentFindingTypes.hasBoneLoss,
    presentFindingTypes.hasCaris,
    presentFindingTypes.hasPeriapicalRadiolucency,
    imagingVisitId,
    selectedTreatments,
    selectedTxGroups,
    setIncludeFindingTypes,
  ]);

  return {
    filteredImages,
    presentFindingTypes,
  };
};
